import { useCallback, useEffect, useRef, useState } from "react"

type SpeechRecognitionState = "idle" | "listening" | "error"

interface UseSpeechRecognitionResult {
  state: SpeechRecognitionState
  transcript: string
  interimTranscript: string
  error: string | null
  isSupported: boolean
  start: () => void
  stop: () => void
  reset: () => void
}

// Tipagem mínima para Web Speech API (não está no lib.dom.d.ts padrão)
interface SpeechRecognitionEvent extends Event {
  resultIndex: number
  results: SpeechRecognitionResultList
}

interface SpeechRecognitionResultList {
  length: number
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
  isFinal: boolean
  [index: number]: { transcript: string; confidence: number }
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string
  message: string
}

interface SpeechRecognitionInstance extends EventTarget {
  lang: string
  continuous: boolean
  interimResults: boolean
  start(): void
  stop(): void
  abort(): void
  onresult: ((ev: SpeechRecognitionEvent) => void) | null
  onerror: ((ev: SpeechRecognitionErrorEvent) => void) | null
  onend: (() => void) | null
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionInstance

function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === "undefined") return null
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor
    webkitSpeechRecognition?: SpeechRecognitionConstructor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export function useSpeechRecognition(): UseSpeechRecognitionResult {
  const Ctor = getSpeechRecognitionConstructor()
  const isSupported = Ctor !== null

  const [state, setState] = useState<SpeechRecognitionState>("idle")
  const [transcript, setTranscript] = useState("")
  const [interimTranscript, setInterimTranscript] = useState("")
  const [error, setError] = useState<string | null>(null)

  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null)

  useEffect(() => {
    if (!Ctor) return

    const recognition = new Ctor()
    recognition.lang = "pt-BR"
    recognition.continuous = false
    recognition.interimResults = true

    recognition.onresult = (event) => {
      let finalText = ""
      let interimText = ""
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        const text = result[0].transcript
        if (result.isFinal) {
          finalText += text
        } else {
          interimText += text
        }
      }
      if (finalText) setTranscript((prev) => prev + finalText)
      setInterimTranscript(interimText)
    }

    recognition.onerror = (event) => {
      setError(event.error || "speech_error")
      setState("error")
    }

    recognition.onend = () => {
      setState((current) => (current === "listening" ? "idle" : current))
      setInterimTranscript("")
    }

    recognitionRef.current = recognition

    return () => {
      recognition.onresult = null
      recognition.onerror = null
      recognition.onend = null
      try {
        recognition.abort()
      } catch {
        // ignora
      }
      recognitionRef.current = null
    }
  }, [Ctor])

  const start = useCallback(() => {
    if (!recognitionRef.current) return
    setTranscript("")
    setInterimTranscript("")
    setError(null)
    setState("listening")
    try {
      recognitionRef.current.start()
    } catch (err) {
      setError((err as Error).message)
      setState("error")
    }
  }, [])

  const stop = useCallback(() => {
    if (!recognitionRef.current) return
    try {
      recognitionRef.current.stop()
    } catch {
      // ignora
    }
  }, [])

  const reset = useCallback(() => {
    setTranscript("")
    setInterimTranscript("")
    setError(null)
    setState("idle")
  }, [])

  return { state, transcript, interimTranscript, error, isSupported, start, stop, reset }
}
